import {z} from "zod/v4-mini"

type AnySchema = z.ZodMiniType

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    if (value === null || typeof value !== "object") {
        return false
    }
    const prototype = Object.getPrototypeOf(value)
    return prototype === Object.prototype || prototype === null
}

// Fills in the `z._default(...)` values of a parameters schema for omitted (undefined) parameters without validating anything.
// Parameters hold graph nodes and other class instances which a real zod parse would reject, hence only plain objects and arrays are descended into.
export const applyParameterDefaults = <Value>(schema: AnySchema, value: Value): Value => {
    const def = schema._zod.def as {type: string; innerType?: AnySchema; shape?: Record<string, AnySchema>; element?: AnySchema; defaultValue?: unknown}
    switch (def.type) {
        case "default":
            // like zod, an omitted value short-circuits to the default without parsing it any further
            return (value === undefined ? def.defaultValue : value) as Value
        case "prefault":
            // like zod, the fallback is parsed by the inner schema, so its own defaults apply as well
            return applyParameterDefaults(def.innerType!, (value === undefined ? def.defaultValue : value) as Value)
        case "optional":
        case "nullable":
            return value === undefined || value === null ? value : applyParameterDefaults(def.innerType!, value)
        case "object": {
            if (!isPlainObject(value)) {
                return value
            }
            const result: Record<string, unknown> = {...value}
            for (const [key, propertySchema] of Object.entries(def.shape!)) {
                const propertyValue = applyParameterDefaults(propertySchema, value[key])
                if (propertyValue !== undefined) {
                    result[key] = propertyValue
                }
            }
            return result as Value
        }
        case "array":
            return (Array.isArray(value) ? value.map((element) => applyParameterDefaults(def.element!, element)) : value) as Value
        default:
            return value
    }
}
